import { retrieveRawInitData } from '@tma.js/sdk-react';
import config from '@/config';
import { triggerUnauthorized } from '@/contexts/authUnauthorized';
import { ApiError, ValidationError } from '@/errors/ApiError';

export interface ApiResponse<T> {
  status: number;
  data: T | null;
}

export default abstract class ApiService {
  protected get headers(): Record<string, string> {
    const base: Record<string, string> = {
      // Default Accept assumes JSON; we may override per request based on responseType
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    const rawInitData = retrieveRawInitData();
    if (rawInitData) {
      base.initData = rawInitData;
    }

    return base;
  }

  protected get apiBaseUrl() {
    return config.apiBaseUrl;
  }

  protected get apiPrefix() {
    return '/api';
  }

  protected async fetch<T = unknown>(
    endpoint: string,
    options: RequestInit & {
      allowedErrorCodes?: number[];
      responseType?: 'json' | 'blob' | 'text';
      skipContentType?: boolean;
      timeoutMs?: number;
      returnNullOn404?: boolean;
      query?: Record<string, unknown>;
    } = {}
  ): Promise<ApiResponse<T>> {
    const {
      allowedErrorCodes,
      responseType = 'json',
      skipContentType = false,
      timeoutMs = 15000,
      returnNullOn404 = true,
      query,
      ...fetchOptions
    } = options;

    const urlObj = new URL(this.apiPrefix + endpoint, this.apiBaseUrl);

    if (query) {
      const appendParam = (key: string, value: unknown) => {
        if (value == null) return;
        if (value === '') return;
        if (Array.isArray(value)) {
          value.forEach((v) => appendParam(key, v));
          return;
        }
        if (value instanceof Date) {
          urlObj.searchParams.append(key, value.toISOString());
          return;
        }
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          urlObj.searchParams.append(key, String(value));
        }
        // skip any other types (object, function, symbol)
      };
      Object.entries(query).forEach(([k, v]) => appendParam(k, v));
    }

    const url = urlObj.toString();

    // Merge headers robustly using Headers API
    const baseHeaders = new Headers(this.headers);

    // Adjust Accept header based on expected response type
    if (responseType === 'blob') baseHeaders.set('Accept', '*/*');
    if (responseType === 'text') baseHeaders.set('Accept', 'text/plain, */*');

    const extraHeaders = new Headers(fetchOptions.headers || undefined);
    // If FormData body or skipContentType, do not send Content-Type (browser sets boundary)
    const isFormData = typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData;
    if (skipContentType || isFormData) {
      baseHeaders.delete('Content-Type');
    }
    // Merge extra headers after base so callers can override
    extraHeaders.forEach((value, key) => baseHeaders.set(key, value));

    // Timeout via AbortController
    const controller = new AbortController();
    const timeoutId = timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : undefined;

    let response: Response;
    try {
      response = await fetch(url, {
        ...fetchOptions,
        headers: baseHeaders,
        signal: controller.signal,
      });
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }

    // Helper to detect empty body without consuming it
    const hasBody = () => {
      if ([204, 205, 304].includes(response.status)) return false;
      const len = response.headers.get('content-length');
      if (len !== null && Number(len) === 0) return false;
      return true; // might still be empty but we'll handle parse errors below
    };

    const contentType = response.headers.get('content-type') || '';

    const parseBody = async (): Promise<T | null> => {
      if (!hasBody()) return null;
      try {
        if (responseType === 'blob') return (await response.blob()) as unknown as T;
        if (responseType === 'text') return (await response.text()) as unknown as T;
        // Default: json; only attempt JSON if content-type indicates JSON
        if (contentType.includes('application/json') || contentType.includes('+json')) {
          return (await response.json()) as T;
        }
        // Fallback: try text to avoid throwing on non-JSON
        const txt = await response.text();
        return txt as unknown as T;
      } catch {
        return null;
      }
    };

    // Error branch
    if (!response.ok && !allowedErrorCodes?.includes(response.status)) {
      if (response.status === 401) {
        triggerUnauthorized();
      }

      if (response.status === 404 && returnNullOn404) {
        return { status: 404, data: null };
      }

      if (!response.ok && !allowedErrorCodes?.includes(response.status)) {
        let errorData: { message?: string; errors?: ValidationError };
        try {
          errorData = (await response.json()) as { message?: string; errors?: ValidationError };
        } catch {
          // Not a JSON response, or other parsing error.
          throw new ApiError(response.statusText);
        }
        throw new ApiError(errorData.message || response.statusText, errorData.errors);
      }

      try {
        if (responseType === 'json') {
          const data = (await response.json()) as T;
          return { status: response.status, data };
        }
        if (responseType === 'blob') {
          const data = (await response.blob()) as T;
          return { status: response.status, data };
        }
        if (responseType === 'text') {
          const data = (await response.text()) as T;
          return { status: response.status, data };
        }

        throw new Error('Unsupported responseType');
      } catch (error) {
        if (error instanceof ApiError) {
          throw error;
        }
        const message = error instanceof Error ? error.message : 'An unknown error occurred';
        throw new ApiError(message);
      }
    }

    // Success/allowed error parsing
    const data = await parseBody();
    return { status: response.status, data };
  }
}
